import { isAndriod, isIOS } from "@repo/shared-utils/mobile-utils";
import { PromptMemoizer } from "./PromptMemoizer";
import { TarsierCommon } from "./TarsierCommon";
import promptConstants from "./constants/prompt.constants.json";
import tarsierConstants from "./constants/tarsier.constants.json";
import {
  IPromptCommonData,
  CombinedClevertapProps,
} from "./types/common.types";

// Re-export the type to maintain public API
export type { CombinedClevertapProps };

const enabledURLs = [
  "/investments",
  "/investments/investment-portfolio",
  "/insurance/",
  "/investments/fixed-deposit",
  "/msme-world",
  "/doctor-loan",
  "/doctor-world",
  "/season-offers-for-you",
  "/electronics-on-emis",
  "/mobiles",
  "/television",
  "/home-appliances-on-emi",
  "/furniture-on-emi",
  "/lifestyle-products-on-emis",
  "/lifestyle-accessories-on-emi",
  "/new-car-loan",
  "/two-wheeler-loan",
  "/tractor-loan",
  "/used-tractor-loan",
  "/loan-against-tractor",
  "/tractor-loan-balance-transfer",
  "/government-schemes",
  "/personal-loan",
  "/loans",
  "/loan-against-securities",
  "/gold-loan",
  "/loan-against-property",
  "/loan-against-property-balance-transfer",
  "/loan-against-shares",
  "/loan-against-mutual-funds",
  "/loan-against-insurance-policy",
  "/esop-financing",
  "/secured-business-loan",
  "/secured-business-loan-balance-transfer",
  "/flexi-loans",
  "/flexi-loan",
  "/easy-emi-consumer-durables-loan",
  "/margin-trade-financing",
  "/myaccount/sce/pdp",
  "/loan-against-fixed-deposit",
  "/auto-world",
  "/home-loan-world",
  "/home-loan",
  "/home-loan-balance-transfer",
  "/bmall/new-cars",
  "/used-car-loan",
  "/loan-against-car",
  "/car-loan-balance-transfer-and-top-up",
  "/tractor-world",
  "/credit-pulse-report",
  "/business-loan",
  "/loan-for-lawyers",
  "/chartered-accountant-loan",
  "/loan-against-bonds",
  "/personal-loan-emi-calculator",
  "/interest-calculator",
  "/doctor-loan-emi-calculator",
  "/business-loan-emi-calculator",
  "/home-loan-emi-calculator",
  "/used-car-loan-emi-calculator",
  "/medical-equipment-finance-emi-calculator",
  "/secured-business-loan-emi-calculator",
  "/how-is-personal-loan-amount-decided",
  "/myaccountlogin?Source=Loan",
  "/terms-of-use",
];

export class Tarsier {
  private readonly _defCommonData: IPromptCommonData = {};
  private readonly memoizer: PromptMemoizer;

  private constructor() {
    this.memoizer = new PromptMemoizer();
    const originalPrompt = window.prompt;
    window.prompt = (...args) => {
      if (
        window.location.href.includes("/app-v") &&
        !(window.Android || window.webkit?.messageHandlers)
      ) {
        return null;
      }
      return originalPrompt(...args);
    };
    const pullDownRefresh =
      document
        .querySelector('meta[name="pull-down-refresh"]')
        ?.getAttribute("content") ?? "false";
    if (isAndriod && pullDownRefresh === "false") {
      window.Android?.setPullRefresh?.(false);
    } else if (isIOS && pullDownRefresh === "true") {
      window.webkit.messageHandlers.setPullRefresh.postMessage(true);
    }
    const appHeader = document.querySelector("header");
    if (!this.invokePrompt(promptConstants.getAppVersion) && appHeader) {
      appHeader.remove();
      document.body.classList.remove("app-header-enabled");
    }

    window.BackPressed = () => {
      if (!this.getHref().includes("/app-v")) return;

      const metaTag = document.querySelector(`meta[name="tag-8"]`);
      const ctEvent = metaTag
        ? metaTag?.getAttribute("content")
        : "DEVICE_BACK";
      this.invokePrompt(
        `triggerCTEvent://${JSON.stringify({ eventName: ctEvent, props: { EP_CTA: "DEVICE_BACK" } })}`,
      );

      const shouldNavigateBack = enabledURLs.some((url) =>
        this.getHref().includes(url),
      );

      if (shouldNavigateBack) {
        this.navigateBackOrHome();
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      if (
        document.body.classList.contains("modal-open") ||
        !urlParams.has("key")
      ) {
        window.history.back();
      } else {
        this.invokeHomePagePrompt();
      }
    };

    this.pushCurrentPageToHistory();

    window.onload = () => {
      this.pushCurrentPageToHistory();
    };

    document.addEventListener("visibilitychange", () => {
      const currentPath = window.location.pathname;
      const includedPages = [
        "/app-v1/game-zone",
        "/app-v1/two-wheeler-loan",
        "/app-v1/investments/open-demat-sso",
      ];
      if (
        includedPages.includes(currentPath) &&
        document.visibilityState === "visible"
      ) {
        window.location.reload();
      }
    });
  }

  static init(): Tarsier {
    return new Tarsier();
  }

  private readonly pushCurrentPageToHistory = () => {
    const excludedPages: Array<string> = [
      "/initiate-tarsier-webview",
      "/balic-cart-redirect",
    ];
    if (
      !this.getHref().includes("/app-v") ||
      excludedPages.some((page) => this.getHref().includes(page))
    )
      return;
    const historyStack = this.readHistoryStack();
    if (
      historyStack.length === 0 ||
      historyStack.at(-1) !== this.getHref()
    ) {
      historyStack.push(this.getHref());
      this.writeHistoryStack(historyStack);
    }
  };

  readHistoryStack = (): Array<string> => {
    const sharedPrefData = {
      action: "read",
      type: "string",
      key: "PAGE_HISTORY_STACK",
      value: "",
    };
    const historyStackPref = JSON.parse(
      (this.invokePrompt(
        `SharedPref://${JSON.stringify(sharedPrefData)}`,
        true,
      ) as string) || "[]",
    );
    return historyStackPref;
  };

  writeHistoryStack = (historyStack: Array<string>) => {
    const sharedPrefData = {
      action: "write",
      type: "string",
      key: "PAGE_HISTORY_STACK",
      value: JSON.stringify(historyStack),
    };
    this.invokePrompt(`SharedPref://${JSON.stringify(sharedPrefData)}`, true);
  };

  private readonly navigateBackOrHome = () => {
    if (document.body.classList.contains("modal-open")) {
      window.history.back();
      return;
    }
    const historyStack = this.readHistoryStack();
    historyStack.pop();
    this.writeHistoryStack(historyStack);
    const previousUrl = historyStack.at(-1);
    if (previousUrl) {
      window.location.replace(previousUrl);
      return;
    }
    this.invokeHomePagePrompt();
  };

  invokeHomePagePrompt = () => {
    const versionNumberSplit = (
      this.invokePrompt(promptConstants.getAppVersion) as string
    )?.split(".");
    const isIosDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    let numberToCheck = versionNumberSplit?.join("");
    if (!isIosDevice) {
      numberToCheck = numberToCheck?.slice(1);
    }
    const parsedVersionNumber = parseInt(numberToCheck, 10);
    const versionToCheck = isIosDevice
      ? tarsierConstants.PARSED_BASE_VERSION_FOR_NEW_REDIRECTION_IOS
      : tarsierConstants.PARSED_BASE_VERSION_FOR_NEW_REDIRECTION;
    if (window.location.href.includes("?referer=HomePage")) {
      this.invokePrompt(promptConstants.homepageRedirection, true);
    }
    this.invokePrompt(
      parsedVersionNumber && parsedVersionNumber > versionToCheck
        ? promptConstants.appBack
        : promptConstants.homepageRedirection,
      true,
    );
  };

  handleAppBack(): void {
    if (window.BackPressed && typeof window.BackPressed === "function") {
      window.BackPressed();
    }
  }

  invokePrompt(prompt: string, force?: boolean): unknown {
    if (!force) {
      const _memoizedPrompt = this.memoizer.read(prompt);
      if (_memoizedPrompt) return _memoizedPrompt;
    }
    const promptResult = window.prompt(prompt);
    this.memoizer.memoize(prompt, promptResult);
    return promptResult;
  }

  getCommonData(): TarsierCommon {
    const rawCommondata = this.invokePrompt(promptConstants.common);
    if (rawCommondata)
      return new TarsierCommon(JSON.parse(rawCommondata as string));
    return new TarsierCommon(this._defCommonData);
  }

  getHref() {
    return window.location.href;
  }
}
